import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  Suspense,
  lazy,
} from "react";
import "./Home.css";
import { useMsal } from "@azure/msal-react";
import bannerVid from "../assets/banner-video.mp4";
import NavBar from "../components/NavBar/Navigation";
import QuickActions from "../components/QuickActions/QuickActions";
import ScrollFloat from "../components/ScrollFloat/ScrollFloat";
import DomeGallery from "../components/DomeGallery/DomeGallery";
import Footer from "../components/Footer/Footer";
import GrowSection from "../components/GrowSection/GrowSection";
import AnalyticsSection from "../components/AnalyticsSection/AnalyticsSection";
import ScrollArrow from "../components/ScrollArrow/ScrollArrow";

/* Lanyard is lazy-loaded.

   It's by far the heaviest thing on this page — three.js, react-three-fiber,
   drei and the rapier physics WASM. Statically imported, all of that sat in
   the main bundle and had to be downloaded, parsed and executed BEFORE React
   could paint anything, while competing with banner-video.mp4 for the same
   connection. Splitting it out means the banner gets the bandwidth first and
   the page paints without waiting on the 3D stack.

   NOTE: this must be a default export from Lanyard.jsx. If that file uses a
   named export instead, use:
     const Lanyard = lazy(() =>
       import("../components/Lanyard/Lanyard").then((m) => ({ default: m.Lanyard }))
     );
*/
const Lanyard = lazy(() => import("../components/Lanyard/Lanyard"));

const B2C_EXT = "extension_de329301d4644655ba91a299e1aa3ea5";

/* ── Local dev account ──────────────────────────────────────────
   localhost isn't registered as a redirect URI in B2C, so MSAL never
   gets an account there and `if (!account) return null` rendered a blank
   page. This stands in for the real account so the page can be worked
   on locally.

   SAFE ON LIVE: gated on NODE_ENV === "development", which CRA/Vite
   hard-code at build time. In a production build the ternary folds to
   `null` and the whole object is dead-code eliminated by the minifier —
   it is not present in the deployed bundle. On live, `accounts[0]` is
   the only thing that can satisfy the guard below, exactly as before.

   If you'd rather not ship the code path at all, delete this const and
   change the line below back to `const account = accounts[0];`. Doing
   that means this file diverges from the local copy again, which is
   what caused the two versions to drift apart in the first place.
   ───────────────────────────────────────────────────────────────── */
const DEV_ACCOUNT =
  process.env.NODE_ENV === "development"
    ? {
        name: "Local Dev User",
        username: "dev@localhost",
        idTokenClaims: {
          [`${B2C_EXT}_Role`]: "Frontend Developer",
          [`${B2C_EXT}_Department`]: "IT",
        },
      }
    : null;

const Home = () => {
  const { accounts } = useMsal();
  // real account always wins; DEV_ACCOUNT is null in production builds
  const account = accounts[0] || DEV_ACCOUNT;
  const [isMobile, setIsMobile] = useState(false);
  const [showLanyard, setShowLanyard] = useState(false);
  const domeBoxRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");

    setIsMobile(mq.matches);

    let timer = null;
    const handler = (e) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setIsMobile(e.matches);
      }, 100);
    };

    mq.addEventListener("change", handler);
    return () => {
      mq.removeEventListener("change", handler);
      clearTimeout(timer);
    };
  }, []);

  /* Give the banner video the highest possible priority.

     fetchPriority is set via setAttribute rather than as a JSX prop because
     React only added first-class support for it in 18.3 — on older versions
     a `fetchPriority` prop is dropped with an unknown-prop warning, and the
     lowercase `fetchpriority` spelling is inconsistent across versions.
     setAttribute works everywhere and is ignored by browsers that don't
     support the hint.

     preload="auto" (set in JSX below) tells the browser to start buffering
     immediately instead of waiting for a play attempt. Combined with the
     lazy Lanyard above, the video is effectively the first heavy resource
     the browser fetches after the HTML and CSS. */
  useLayoutEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.setAttribute("fetchpriority", "high");
    // some browsers won't begin buffering until load() is called explicitly
    if (v.preload === "auto" && v.readyState === 0) v.load();
  }, []);

  /* Mount Lanyard only AFTER the banner has enough data to render.

     Three staggered triggers, whichever fires first wins:
       1. the video's `loadeddata` event — the banner is visibly playing, so
          the 3D work no longer competes with it
       2. requestIdleCallback — covers a cached video that fired loadeddata
          before this effect ever attached its listener
       3. a 3000ms hard fallback — so a failed/blocked video (corporate proxy,
          codec issue) can never leave the lanyard permanently unmounted

     The chunk itself is also prefetched during idle time, so by the time
     showLanyard flips the JS is usually already in cache and there's no
     visible gap. */
  useEffect(() => {
    let done = false;
    const reveal = () => {
      if (done) return;
      done = true;
      setShowLanyard(true);
    };

    const v = videoRef.current;
    if (v) {
      if (v.readyState >= 2) reveal();
      else v.addEventListener("loadeddata", reveal, { once: true });
    }

    const idle =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(reveal, { timeout: 2000 })
        : setTimeout(reveal, 1200);

    const hardFallback = setTimeout(reveal, 3000);

    // warm the chunk without blocking anything
    const prefetch =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(() => {
            import("../components/Lanyard/Lanyard");
          })
        : setTimeout(() => {
            import("../components/Lanyard/Lanyard");
          }, 1500);

    return () => {
      done = true;
      if (v) v.removeEventListener("loadeddata", reveal);
      if (typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idle);
        window.cancelIdleCallback(prefetch);
      } else {
        clearTimeout(idle);
        clearTimeout(prefetch);
      }
      clearTimeout(hardFallback);
    };
  }, []);

  /* Measure where the dome is ACTUALLY painted inside its box.

     Only <img> tiles are measured. The version previously on live walked
     every descendant with querySelectorAll("*"), which picked up
     DomeGallery's sphere/stage wrappers — boxes much larger than the
     visible dome — so both offsets came back ~0, nothing got cancelled,
     and the heading landed on top of the dome while a huge gap opened
     above the footer. That is the globe bug on live.

     The two vertical offsets are SIGNED:
       --dg-fit-top     distance from box top DOWN to the highest tile.
                        NEGATIVE when the sphere spills above the box
                        (which it does — the tiles are wider than their
                        container). CSS subtracts this from
                        --dome-heading-gap, so a negative value
                        correctly pushes the box down instead of letting
                        it ride up over the heading. Do not clamp it —
                        the old live code wrapped this in Math.max(0, …)
                        and that clamp is precisely what threw the top
                        spacing away.
       --dg-fit-bottom  distance from the lowest tile DOWN to box
                        bottom. Cancelled with a negative margin so the
                        footer gap is measured from the visible dome.

     --dg-fit-x  (MOBILE ONLY, new)
       The sphere's painted tiles are not perfectly symmetrical about
       the box's centre line — which limb tiles are facing you depends
       on the rotation the sphere settles at, so the globe reads as
       sitting slightly right of centre even though the BOX is centred
       perfectly. This is the horizontal distance from the painted
       centre of the dome to the centre of its box, applied back as a
       translateX so the visible globe is centred rather than the empty
       container.

       It's invariant under its own correction: the transform moves the
       box and its tiles together, so re-measuring after applying it
       yields the same number — no accumulation, no drift. Clamped to
       ±48px so a half-decoded sphere can never yank it sideways.

     clampToBox (MOBILE ONLY)
       On mobile the dome is sized to fill its box, so tiles can spill
       past the edges and the section's `overflow: clip` cuts them off.
       Once that happens the painted bounds are no longer the VISIBLE
       bounds, and feeding a negative spill into the margins would shove
       the box down and reopen the exact gap this mechanism exists to
       close. So the two vertical offsets are floored at 0 on mobile
       only. Desktop keeps the signed behaviour untouched.

     getBoundingClientRect reflects the 3D transforms and ignores the
     overflow clip, so these are the real painted bounds.

     Re-measured on mount, after each tile image decodes, at 600ms and
     1500ms (the sphere keeps settling), on resize/orientationchange,
     and via a ResizeObserver on the box — which catches the mobile
     URL-bar collapse, since that doesn't reliably fire a resize. Not on
     drag — rotation doesn't change the overall bounds enough to matter,
     and re-centring mid-spin would look like a glitch. */
  useLayoutEffect(() => {
    const box = domeBoxRef.current;
    if (!box) return;

    const clampToBox = isMobile;

    const measure = () => {
      const boxRect = box.getBoundingClientRect();
      if (!boxRect.height) return;

      const tiles = box.querySelectorAll("img");
      if (!tiles.length) return;

      let topMost = Infinity;
      let bottomMost = -Infinity;
      let leftMost = Infinity;
      let rightMost = -Infinity;

      tiles.forEach((img) => {
        const r = img.getBoundingClientRect();
        // skip tiles that haven't laid out / decoded yet
        if (r.width < 4 || r.height < 4) return;
        if (r.top < topMost) topMost = r.top;
        if (r.bottom > bottomMost) bottomMost = r.bottom;
        if (r.left < leftMost) leftMost = r.left;
        if (r.right > rightMost) rightMost = r.right;
      });

      if (!Number.isFinite(topMost) || !Number.isFinite(bottomMost)) return;

      // signed — negative means the dome spills past that edge
      let fitTop = Math.round(topMost - boxRect.top);
      let fitBottom = Math.round(boxRect.bottom - bottomMost);

      if (clampToBox) {
        fitTop = Math.max(0, fitTop);
        fitBottom = Math.max(0, fitBottom);

        if (Number.isFinite(leftMost) && Number.isFinite(rightMost)) {
          const domeCentreX = (leftMost + rightMost) / 2;
          const boxCentreX = boxRect.left + boxRect.width / 2;
          const fitX = Math.max(
            -48,
            Math.min(48, Math.round(boxCentreX - domeCentreX))
          );
          box.style.setProperty("--dg-fit-x", `${fitX}px`);
        }
      }

      box.style.setProperty("--dg-fit-top", `${fitTop}px`);
      box.style.setProperty("--dg-fit-bottom", `${fitBottom}px`);
    };

    const raf = requestAnimationFrame(measure);
    const settle = setTimeout(measure, 600);
    const settleLate = setTimeout(measure, 1500);
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(box);
    }

    // re-measure as tiles finish decoding — bounds change as they land.
    // Matters more on live than locally: images come off the network
    // there, so on a cold cache they land well after first paint.
    const imgs = Array.from(box.querySelectorAll("img"));
    imgs.forEach((img) => {
      if (!img.complete) img.addEventListener("load", measure);
    });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      clearTimeout(settleLate);
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      if (ro) ro.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", measure));
    };
  }, [isMobile]);

  if (!account) return null;

  // Removed: console.log("B2C Claims", JSON.stringify(claims, null, 2))
  // It printed the full B2C id-token claim set (name, email, oid, custom
  // extension attributes) into the browser console on every render, in
  // production too. If you need it while debugging claims, gate it:
  //   if (process.env.NODE_ENV === "development") console.log(...)
  const claims = account.idTokenClaims || {};

  const role =
    claims[`${B2C_EXT}_Role`] ||
    claims[`${B2C_EXT}_JobTitle`] ||
    claims.jobTitle ||
    "Not Set";

  const department =
    claims[`${B2C_EXT}_Department`] ||
    claims.department ||
    "Not Set";

  return (
    <div className="home">
      {/* Banner Section */}
      <div className="home-banner">
        <video
          ref={videoRef}
          className="banner-image"
          src={bannerVid}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        />

        <NavBar />

        {/* Lanyard now renders on mobile too — resized via the
            .lanyard-wrapper CSS scale() at the ≤768px breakpoint in
            Home.css, rather than being hidden. If the resize looks off
            (blurry canvas, wrong camera framing, physics feeling too
            floaty/cramped at the smaller scale), that likely needs a
            fix inside Lanyard.jsx's own camera/canvas setup rather than
            a CSS-only tweak — share that file if so.

            Suspense fallback is null on purpose: the overlay is
            transparent and sits over the video, so an empty slot reads
            as "not there yet" rather than as a broken element. A
            spinner here would draw the eye to the one part of the
            banner that isn't ready. */}
        <div className="lanyard-overlay">
          {showLanyard && (
            <Suspense fallback={null}>
              <Lanyard
                key={`lanyard-${isMobile}`}
                position={[0, 0, 20]}
                gravity={[0, -40, 0]}
                transparent={true}
                name={account.name}
                positionText={role}
                section={department}
              />
            </Suspense>
          )}
        </div>

        <QuickActions />
      </div>

      {/* ScrollArrow stays desktop-only */}
      {!isMobile && <ScrollArrow />}

      {/* NOTE: <ScrollFloat> has no children, so this whole block only
          contributes vertical padding right now (same as before —
          left untouched so spacing doesn't shift). Drop text between
          the tags when you want the heading back. */}
      <div className="centered-section hero-text-section">
        <div className="text-section">
          <ScrollFloat
            animationDuration={1}
            ease="back.inOut(2)"
            scrollStart="center bottom+=50%"
            scrollEnd="bottom bottom-=40%"
            stagger={0.06}
          >
          </ScrollFloat>
        </div>
      </div>

      <GrowSection />

      <div className="bottom-bg-color">
        <AnalyticsSection />
      </div>

      {/* Globe Section */}
      <div className="centered-section globe-mobile-section">
        <div className="globe-text">
          <h2>
            What <span className="green-text"> we </span> are all about...
            <span className="pg-text"></span>
          </h2>
        </div>

        <div className="centered-col">
          <div className="globe-section" ref={domeBoxRef}>
            {/* ── MOBILE DOME SIZING ───────────────────────────────
                DomeGallery's radius is `basis * fit`, clamped to
                [minRadius, maxRadius] and capped at boxHeight * 1.35.
                The painted globe comes out at roughly that radius, so
                `fit` is effectively "what fraction of the sizing basis
                the globe occupies".

                fitBasis="width" on mobile so the basis is the column
                width, not whichever side happens to be shorter — that
                flip (it switches at aspect 1.3) is what made the globe
                size jump between phone widths.

                fit 0.72 → globe paints at ~72% of the column width.
                Raise toward 0.85 for a bigger globe, drop toward 0.6
                for a smaller one. This is THE size knob; leave the
                radii alone unless you're going far outside that range.

                minRadius 180 / maxRadius 520 are just guard rails —
                the old 380 ceiling was clamping wider phones back down
                and making the size inconsistent across devices.

                segments 20 (was 18) — slightly finer, so the tiles at
                the limb are less stretched. dragDampening 3 (was 4.5),
                which felt dead to a flick on touch.

                Desktop props are exactly as they were.

                `key` forces a clean rebuild when the breakpoint is
                crossed, so the sphere re-derives its geometry instead
                of interpolating from the other layout's numbers. */}
            <DomeGallery
              key={`dome-${isMobile}`}
              fit={isMobile ? 0.72 : 0.88}
              fitBasis={isMobile ? "width" : "auto"}
              minRadius={isMobile ? 180 : 350}
              maxRadius={isMobile ? 520 : 750}
              maxVerticalRotationDeg={0}
              segments={isMobile ? 20 : 28}
              dragDampening={isMobile ? 3 : 2}
              grayscale={false}
            />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Home;